import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { EMPTY, Observable, Subject, combineLatest, forkJoin, of, throwError } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, switchMap, take, tap, distinctUntilKeyChanged } from 'rxjs/operators';
import { TrainingType, ICampaign, IELearningData, IFormation, IUser, Status, ITimeInterval, IMasterclass, IChapter, ICourse, IModule, IMasterclassVersion, IQuestion } from '@shared';
import { BackOfficeService } from './back-office.service';
import { FirebaseService } from 'src/app/firebase/firebase.service';
import { SubSink } from 'subsink';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { ELearningDataParsingService, ICompletedModule } from '../e-learning/parsing-service/e-learning-data-parsing.service';
import { isModuleCompleted } from '../e-learning/e-learning-helpers';
import { buildCreditedConnections, getCreditedMinutes } from '../e-learning/parsing-service/connection-schedule';
import { NO_FORMATIONS, PRIVATE_LESSON_DEDUCTION_HOURS, TRAINING_DURATION_VARIATION } from './back-office-conf';
import * as moment from 'moment';
import { NewSubscriberDialogComponent } from './users/new-subscriber-dialog/new-subscriber-dialog.component';
import { BackOfficeELearningService } from './e-learning/back-office-e-learning.service';
import { ELearningElementInfo, IELearningElement, ISelectedELearningElement } from './models/ELearningElement';
import { v4 as uuid } from 'uuid';
import { ConfirmVersionChangeDialogComponent } from './e-learning/confirm-version-change-dialog/confirm-version-change-dialog.component';
import * as _ from 'lodash';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { UserRoleEnum } from '@shared';
import { PromoteUserDialogComponent } from './users/promote-user-dialog/promote-user-dialog.component';
import { EditUserFormDialogComponent } from './users/edit-user-form-dialog/edit-user-form-dialog.component';
import { NewVersionFormDialogComponent } from './e-learning/new-version-form-dialog/new-version-form-dialog.component';
import { waitForImages } from './utils/wait-for-images';
import { confirmDuplicateCourseIds } from './utils/e-learning-duplicate-ids';
import { getAssignedTrainerIds, getTrainerNames, isAssignedToTrainer } from './utils/trainer-assignment';
import { hasTrainingDetails } from './utils/formation-display';
import { PRINT_IMAGES_TIMEOUT_MS } from '../e-learning/parsing-service/e-learning-export-conf';

type BackOfficeTab = 'campaigns' | 'users' | 'masterclasses';
@Component({
  selector: 'app-back-office',
  templateUrl: './back-office.component.html',
  styleUrls: ['./back-office.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed, void', style({ height: '0px' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
      transition('expanded <=> void', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)'))
    ])
  ],
  encapsulation: ViewEncapsulation.None
})
export class BackOfficeComponent implements OnInit {
  campaigns$: Observable<ICampaign[]> = new Observable();
  campaigns: ICampaign[] = [];
  users$: Observable<IUser[]> = new Observable();
  users: IUser[] = [];
  masterclassVersions$: Observable<IMasterclassVersion[]> = new Observable();
  masterclassVersions: IMasterclassVersion[] = [];
  selectedMasterclasses: IMasterclass[] = [];
  userChanged: Subject<IUser> = new Subject<IUser>();

  campaignDataColumns: string[] = ['orderNumber', 'status', 'creationDate', 'TTCBudget', 'email', 'name', 'phoneNumber', 'type', 'platform', 'countries', 'links', 'toSpendBudget', 'commission'];
  userDataColumns: string[] = ['email', 'surname', 'name', 'type', 'trainer', 'report', 'resetPassword', 'reportMCQ', 'edit', 'delete'];
  trainerUserDataColumns: string[] = ['email', 'surname', 'name', 'type', 'edit', 'reportMCQ', 'resetPassword'];
  statusOptions: Status[] = ['cancelled', 'waiting', 'ongoing', 'completed', 'old'];
  trainingTypeOptions: TrainingType[] = [
    '35h CPF IA',
    '40h CPF Réseaux sociaux',
    '40h CPF Structurer activité',
    '42h CPF Créer entreprise',
    '45h CPF MAO',
    '49h CPF IA',
    '50h CPF Video',
    '70h CPF Réseaux sociaux',
    '70h CPF Structurer activité',
    '70h AFDAS Développer activité',
    '80h CPF MAO',
    '80h CPF Promouvoir activité visuelle',
    '90h AFDAS MAO',
    '166h AFDAS Développer activité',
    'Facture'
  ];
  trainerOptions: IUser[] = [];
  campaignDataSource: MatTableDataSource<ICampaign>;
  userDataSource: MatTableDataSource<IUser>;

  expandedCampaign: ICampaign | null;
  expandedUser: IUser | null;

  isExportV2Enabled = false;

  selectedELearningElement: ISelectedELearningElement;
  selectedMasterclassVersion: IMasterclassVersion; // The one selected for the back-office
  previousSelectedMasterclassVersion: IMasterclassVersion;
  selectedELearningVersion: IMasterclassVersion; // The one selected for the e-learning

  isMasterclassFormDirty = false;

  @ViewChild('campaignPaginator', { static: false }) campaignTablepaginator: MatPaginator;
  @ViewChild('userPaginator', { static: false }) userTablepaginator: MatPaginator;
  @ViewChild('masterclassPaginator', { static: false }) masterclassTablepaginator: MatPaginator;
  @ViewChild(MatSort) set matSort(sort: MatSort) {
    if (!sort) {
      return;
    }
    if (!this.campaignDataSource.sort) {
      this.campaignDataSource.sort = sort;
    }
  }

  campaignInputFilter = '';
  userInputFilter = '';
  hideFinishedCampaigns: boolean = false;

  email = '';
  password = '';
  loading = false;
  loadingUsers = true;
  currentUser: IUser | null = null;

  currentTab: BackOfficeTab = 'campaigns';

  masterclassLoading = false;

  selectedRole = '';
  filteredValue = '';

  UserRoleEnum = UserRoleEnum;

  showPassword = false;

  userInput$ = new Subject<KeyboardEvent | string>();

  subs = new SubSink();

  constructor(
    private backOfficeService: BackOfficeService,
    public firebaseService: FirebaseService,
    public dialog: MatDialog,
    private router: Router,
    private eLearningDataParsingService: ELearningDataParsingService,
    public backOfficeELearningService: BackOfficeELearningService,
    private angularFireAuth: AngularFireAuth
  ) {
    this.userChanged.pipe(debounceTime(400), distinctUntilChanged()).subscribe((user) => {
      if (!user) return of();
      this.updateUser(user);
      this.userChanged.next(undefined);
      return of();
    });
  }

  ngOnInit(): void {
    this.campaigns$ = this.backOfficeService.getCampaigns();
    this.users$ = this.backOfficeService.getUsers();
    this.masterclassVersions$ = this.backOfficeService.getMasterclassVersions();
    this.subs.sink = this.backOfficeService
      .getTrainingTypeOptions()
      .pipe(catchError(() => of([] as TrainingType[])))
      .subscribe((trainingTypeOptions) => {
        if (trainingTypeOptions.length) {
          this.trainingTypeOptions = trainingTypeOptions;
        }
      });
    this.subs.sink = this.users$.subscribe((users: IUser[]) => {
      this.trainerOptions = users.filter((user) => user.role === 'trainer');
    });
    this.userInput$.pipe(debounceTime(500), distinctUntilChanged()).subscribe((event) => {
      if (typeof event === 'string') {
        this.applyUserDataFilter(event);
      } else {
        this.applyUserDataFilter((event.target as HTMLInputElement).value);
      }
    });
  }

  togglePasswordVisibility(event: Event): void {
    event.preventDefault();
    this.showPassword = !this.showPassword;
  }

  applyCampaignDataFilter(event: Event) {
    this.filterFinishedCampaigns(false);
    const filterValue = (event.target as HTMLInputElement).value.trim().toLowerCase();
    this.campaignInputFilter = filterValue;
    this.campaignDataSource.filter = filterValue.trim().toLowerCase();

    if (this.campaignDataSource.paginator) {
      this.campaignDataSource.paginator.firstPage();
    }
  }

  applyUserDataFilter(value: string) {
    this.filteredValue = value.trim().toLowerCase();
    if (this.filteredValue === 'exportv2') {
      alert('Export version 2 activé !');
      this.isExportV2Enabled = true;
      this.userInputFilter = '';
    } else {
      this.userInputFilter = this.filteredValue;
    }
    this.userDataSource.filter = this.userInputFilter ? this.userInputFilter : JSON.stringify('');

    if (this.userDataSource.paginator) {
      this.userDataSource.paginator.firstPage();
    }
  }

  applyFilter() {
    const filterValue = this.selectedRole ? this.selectedRole.toLowerCase() : this.userInputFilter;

    if (this.userDataSource.filter !== '') {
      this.userDataSource.filter = filterValue + '';
    } else {
      this.userDataSource.filter = JSON.stringify(filterValue);
    }

    if (this.userDataSource.paginator) {
      this.userDataSource.paginator.firstPage();
    }
  }

  filterFinishedCampaigns(hideFinishedCampaigns: boolean) {
    this.hideFinishedCampaigns = hideFinishedCampaigns;
    this.campaignInputFilter = '';
    if (hideFinishedCampaigns) {
      this.campaignDataSource.filter = 'ing'; // waiting, ongoing (this is clearly a hack for MatDataSource filtering)
    } else {
      this.campaignDataSource.filter = '';
    }
  }

  onStatusChange(campaignId: string, newStatus: Status) {
    const newCampaign = this.campaigns.find((campaign) => campaign.id === campaignId);
    if (newCampaign) {
      newCampaign.status = newStatus;
      this.subs.sink = this.backOfficeService.updateCampaign(newCampaign).pipe(take(1)).subscribe();
    } else {
      console.error('An error occured : no campaign found for id ', campaignId);
    }
  }

  onNotesChange(campaignId: string, newNotes: string): void {
    const campaignToUpdate = this.campaigns.find((campaign) => campaign.id === campaignId);
    if (campaignToUpdate) {
      campaignToUpdate.notes = newNotes;
      this.subs.sink = this.backOfficeService
        .updateCampaign(campaignToUpdate)
        .pipe(take(1))
        .subscribe({
          next: () => console.log('Notes successfully updated'),
          error: (err) => console.error('An error occurred while updating the notes: ', err)
        });
    } else {
      console.error('An error occurred: no campaign found for id ', campaignId);
    }
  }

  onTrainerChange(userId: string, newTrainerIds: string[]) {
    const newUser = this.users.find((user) => user.id === userId);
    if (newUser) {
      newUser.trainerIds = newTrainerIds;
      newUser.trainerId = newTrainerIds[0] ?? '';
      this.subs.sink = this.backOfficeService.updateUser(newUser).pipe(take(1)).subscribe();
    } else {
      console.error('An error occured : no user found for id ', userId);
    }
  }

  onLearningPeriodChange(user: IUser, newLearningPeriod: ITimeInterval): void {
    let newELearningData: IELearningData = {
      id: user.id,
      learningPeriod: newLearningPeriod,
      completedCourses: [],
      connections: [],
      connectionsV2: [], // Vide car la période change
      trainingType: '45h CPF MAO'
    };

    if (user.eLearningData) {
      user.eLearningData.learningPeriod = newLearningPeriod;
      user.eLearningData.connectionsV2 = [];
      newELearningData = user.eLearningData;
    }

    user.eLearningData = newELearningData;
    this.subs.sink = this.backOfficeService.updateELearningDataForUser(user.id, newELearningData).subscribe();
  }
  private newFormationId(): string {
    return `formation-${Date.now()}-${Math.round(Math.random() * 1e6)}`;
  }

  private ensureELearningData(user: IUser): IELearningData {
    if (!user.eLearningData) {
      user.eLearningData = {
        id: user.id,
        completedCourses: [],
        connections: [],
        connectionsV2: [],
        trainingType: '45h CPF MAO'
      };
    }
    if (!user.eLearningData.formations) {
      user.eLearningData.formations = [];
    }
    return user.eLearningData;
  }

  // Chatbase and the student portal only read the root fields, so they mirror the most recent formation
  private syncRootFieldsFromFormations(eLearningData: IELearningData): void {
    const formations = eLearningData.formations || [];
    if (!formations.length) return;
    const mostRecent = [...formations].sort((a, b) => new Date(b.learningPeriod?.end || 0).getTime() - new Date(a.learningPeriod?.end || 0).getTime())[0];
    if (mostRecent.learningPeriod?.start && mostRecent.learningPeriod?.end) {
      eLearningData.learningPeriod = mostRecent.learningPeriod;
    }
    if (mostRecent.trainingType) {
      eLearningData.trainingType = mostRecent.trainingType;
    }
  }

  private saveFormations(user: IUser): void {
    const eLearningData = this.ensureELearningData(user);
    this.syncRootFieldsFromFormations(eLearningData);
    this.subs.sink = this.backOfficeService.updateELearningDataForUser(user.id, eLearningData).subscribe(() => {
      // An emptied list must immediately re-fetch the platform suggestions instead of waiting for a collapse/expand
      if (!eLearningData.formations?.length) {
        this.prefilledFormationsByUserId.delete(user.id);
        this.refreshExpandedELearningData(user);
      }
    });
  }

  // Prefill lives outside eLearningData so unrelated saves (trainer, root period) can never persist it by accident
  private prefilledFormationsByUserId = new Map<string, IFormation[]>();
  private newPlatformFormationsByUserId = new Map<string, IFormation[]>();

  // The users table embeds a stale copy of eLearningData: the expanded row must read the real one from the API
  toggleUserExpansion(user: IUser): void {
    this.expandedUser = this.expandedUser === user ? null : user;
    if (this.expandedUser !== user || this.currentUser?.role !== 'admin') {
      return;
    }
    this.refreshExpandedELearningData(user);
  }

  private refreshExpandedELearningData(user: IUser): void {
    this.subs.sink = this.backOfficeService.getELearningDataForUser(user.id).subscribe({
      next: (data) => {
        if (!data) return;
        const eLearningData = this.ensureELearningData(user);
        eLearningData.formations = (data.formations || []).map((formation) => this.parseFormationDates(formation));
        const suggestedFormations = (data.suggestedFormations || []).map((formation) => this.parseFormationDates(formation));
        if (!eLearningData.formations.length && suggestedFormations.length) {
          this.prefilledFormationsByUserId.set(user.id, suggestedFormations);
        }
        this.newPlatformFormationsByUserId.set(user.id, eLearningData.formations.length ? suggestedFormations : []);
      },
      // log only: no e-learning doc yet for this user, the masterclass prefill still applies
      error: (error) => console.log('Pas de données e-learning pour', user.email, error)
    });
  }

  // HTTP JSON carries dates as strings, and the date-picker calls getTime() on them
  private parseFormationDates(formation: IFormation): IFormation {
    return {
      ...formation,
      learningPeriod: formation.learningPeriod?.start && formation.learningPeriod?.end ? { start: new Date(formation.learningPeriod.start), end: new Date(formation.learningPeriod.end) } : undefined,
      connectionsV2: (formation.connectionsV2 || []).map((connection) => ({ start: new Date(connection.start), end: new Date(connection.end) }))
    };
  }

  getFormationsForUser(user: IUser): IFormation[] {
    const persistedFormations = user.eLearningData?.formations;
    if (persistedFormations?.length) {
      return persistedFormations;
    }
    const obtainedMasterclasses = user.eLearningData?.obtainedMasterclasses || [];
    if (!obtainedMasterclasses.length) {
      return persistedFormations || [];
    }
    let prefilled = this.prefilledFormationsByUserId.get(user.id);
    if (!prefilled) {
      prefilled = obtainedMasterclasses.map((masterclassId) => ({
        id: this.newFormationId(),
        masterclassIds: [masterclassId],
        connectionsV2: []
      }));
      this.prefilledFormationsByUserId.set(user.id, prefilled);
    }
    return prefilled;
  }

  shouldShowRootTraining(user: IUser): boolean {
    return !user.eLearningData?.formations?.length && !this.getFormationsForUser(user).some(hasTrainingDetails);
  }

  hasUnsavedSuggestedFormations(user: IUser): boolean {
    return !user.eLearningData?.formations?.length && !!this.prefilledFormationsByUserId.get(user.id)?.some(hasTrainingDetails);
  }

  getNewPlatformFormations(user: IUser): IFormation[] {
    return this.newPlatformFormationsByUserId.get(user.id) || NO_FORMATIONS;
  }

  addNewPlatformFormations(user: IUser): void {
    const eLearningData = this.ensureELearningData(user);
    eLearningData.formations = [...eLearningData.formations, ...this.getNewPlatformFormations(user)];
    this.newPlatformFormationsByUserId.delete(user.id);
    this.saveFormations(user);
  }

  saveSuggestedFormations(user: IUser): void {
    this.adoptPrefilledFormations(user);
    this.saveFormations(user);
  }

  private adoptPrefilledFormations(user: IUser): void {
    const eLearningData = this.ensureELearningData(user);
    if (!eLearningData.formations.length) {
      const prefilled = this.prefilledFormationsByUserId.get(user.id);
      if (prefilled?.length) {
        eLearningData.formations = prefilled;
      }
    }
    this.prefilledFormationsByUserId.delete(user.id);
  }

  onFormationPeriodChange(user: IUser, formation: IFormation, newLearningPeriod: ITimeInterval): void {
    this.adoptPrefilledFormations(user);
    formation.learningPeriod = newLearningPeriod;
    formation.connectionsV2 = [];
    this.saveFormations(user);
  }

  onFormationTrainingTypeChange(user: IUser, formation: IFormation, newTrainingType: TrainingType): void {
    this.adoptPrefilledFormations(user);
    formation.trainingType = newTrainingType;
    formation.connectionsV2 = [];
    this.saveFormations(user);
  }

  onFormationMasterclassesChange(user: IUser, formation: IFormation, masterclassIds: string[]): void {
    this.adoptPrefilledFormations(user);
    formation.masterclassIds = masterclassIds;
    this.saveFormations(user);
  }

  deleteFormation(user: IUser, formation: IFormation): void {
    const prefilled = this.prefilledFormationsByUserId.get(user.id);
    if (!user.eLearningData?.formations?.length && prefilled) {
      this.prefilledFormationsByUserId.set(user.id, prefilled.filter((f) => f.id !== formation.id));
      return;
    }
    const eLearningData = this.ensureELearningData(user);
    eLearningData.formations = eLearningData.formations.filter((f) => f.id !== formation.id);
    this.saveFormations(user);
  }

  onTrainingTypeChange(user: IUser, newTrainingType: TrainingType): void {
    let newELearningData: IELearningData = {
      id: user.id,
      completedCourses: [],
      connections: [],
      connectionsV2: [], // Vide pour forcer la régénération
      trainingType: newTrainingType
    };

    if (user.eLearningData) {
      user.eLearningData.trainingType = newTrainingType;
      // IMPORTANT: Vider connectionsV2 pour forcer la régénération avec le nouveau type
      user.eLearningData.connectionsV2 = [];
      newELearningData = user.eLearningData;
    }

    user.eLearningData = newELearningData;
    this.subs.sink = this.backOfficeService.updateELearningDataForUser(user.id, newELearningData).subscribe();
  }

  openNewSubscriberDialog(trainerId?: string): void {
    this.dialog
      .open(NewSubscriberDialogComponent, {
        width: '400px',
        panelClass: 'new-subscriber-dialog'
      })
      .afterClosed()
      .subscribe((uid: string) => {
        if (!uid) {
          return;
        }
        this.subs.sink = this.backOfficeService.getUserById(uid).subscribe((user: IUser) => {
          user.trainerId = trainerId ?? '';
          user.trainerIds = trainerId ? [trainerId] : [];
          this.userDataSource.data.push(user);
          this.loadUsers();
          if (trainerId) {
            this.onTrainerChange(user.id, [trainerId]);
          }
        });
      });
  }

  openEditUserDialog(user: IUser): void {
    this.dialog
      .open(EditUserFormDialogComponent, {
        width: '700px',
        panelClass: 'user-edit-form-dialog',
        data: {
          currentUser: user,
          masterclasses: this.selectedMasterclassVersion?.masterclasses || []
        }
      })
      .afterClosed()
      .subscribe((mode) => {
        if (mode === 'save') {
          this.loadingUsers = true;
          this.loadUsers();
          this.userInput$.next('');
          this.selectedRole = '';
        }
      });
  }

  translateStatus(status: Status) {
    if (status === 'cancelled') {
      return { value: 'Annulée', color: 'lightgrey' };
    }
    if (status === 'waiting') {
      return { value: 'En attente', color: 'orange' };
    }
    if (status === 'ongoing') {
      return { value: 'En cours', color: 'blue' };
    }
    if (status === 'old') {
      return { value: 'Ancien apprenant', color: 'lightgrey' };
    }
    return { value: 'Terminée', color: 'limegreen' };
  }

  async login() {
    this.loading = true;
    await this.firebaseService
      .signIn(this.email, this.password)
      .then(() => {
        this.access();
      })
      .catch((err) => {
        if (err.code === 'auth/invalid-email') {
          window.alert(`Une erreur est survenue: l'adresse e-mail est invalide`);
        } else if (err.code === 'auth/wrong-password') {
          window.alert(`Une erreur est survenue: le mot de passe est invalide`);
        } else {
          window.alert(`Une erreur est survenue: ${err}`);
        }
      });
    this.email = '';
    this.password = '';
    this.loading = false;
  }

  async access() {
    this.firebaseService.getCurrentUserProperties().subscribe((user) => {
      this.currentUser = user;
      if (this.currentUser) {
        if (this.currentUser?.role === 'admin') {
          this.loadUsers();
          this.loadCampaigns();
          this.loadMasterclasses();
        } else if (this.currentUser?.role === 'trainer') {
          this.loadUsers();
          this.updateTabIndex(1);
          this.loadMasterclasses();
        } else {
          window.alert(`Vous n'avez pas les droits d'accès requis pour accéder au back-office`);
          this.signOut();
        }
      }
    });
  }

  async signInWithGoogle() {
    await this.firebaseService.signInWithGoogle().then(async () => {
      await this.angularFireAuth.setPersistence('local');
      this.access();
    });
    this.email = '';
    this.password = '';
  }

  refreshUsers(): void {
    this.loadingUsers = true;
    this.loadUsers();
    this.selectedRole = '';
  }

  loadUsers(): void {
    this.expandedUser = null;
    this.subs.sink = combineLatest([this.users$, this.backOfficeService.getELearningDataForAll()])
      .pipe(
        switchMap(([users, eLearningDatas]) => {
          eLearningDatas.forEach((data) => {
            if (data.learningPeriod) {
              data.learningPeriod.start = new Date(data.learningPeriod.start);
              data.learningPeriod.end = new Date(data.learningPeriod.end);
            }
            data.formations = (data.formations || []).map((formation) => this.parseFormationDates(formation));
          });

          console.log('this.currentUser.role= ', this.currentUser.role);

          if (this.currentUser.role === UserRoleEnum.TRAINER) {
            users = users.filter((user) => isAssignedToTrainer(user, this.currentUser.id));
          }

          this.users = users.map((user) => ({
            ...user,
            trainerIds: getAssignedTrainerIds(user),
            eLearningData: eLearningDatas.find((data) => data.id === user.id)
          }));

          this.userDataSource = new MatTableDataSource(this.users);

          this.userDataSource.filterPredicate = (data: any, filter: string) => {
            const searchText = this.userInputFilter.toLowerCase();
            const isRoleMatch = this.selectedRole ? data.role.toLowerCase() === this.selectedRole.toLowerCase() : true;
            const isTextMatch = Object.keys(data).some((key) => data[key] && data[key].toString().toLowerCase().includes(searchText));

            return isRoleMatch && isTextMatch;
          };

          this.userDataSource.paginator = this.userTablepaginator;
          this.loadingUsers = false;
          return of(users);
        })
      )
      .subscribe();
  }

  loadCampaigns(): void {
    this.subs.sink = this.campaigns$.subscribe((campaigns) => {
      this.campaigns = campaigns;

      this.campaignDataSource = new MatTableDataSource(campaigns);
      this.campaignDataSource.paginator = this.campaignTablepaginator;
    });
  }

  loadMasterclasses(): void {
    this.masterclassLoading = true;
    this.subs.sink = combineLatest([this.masterclassVersions$, this.firebaseService.getRemoteConfig()])
      .pipe(
        switchMap(([versions, remoteConfig]) => {
          this.masterclassVersions = versions;
          this.masterclassVersions = this.masterclassVersions.filter((mv) => !mv.isTemporary);
          const selectedRemoteConfigVersion = versions.find((version) => remoteConfig.parameters['selectedELearningVersion'].defaultValue.value === version.id);
          this.selectedELearningVersion = selectedRemoteConfigVersion;
          this.selectVersion(selectedRemoteConfigVersion);
          this.masterclassLoading = false;
          return of(selectedRemoteConfigVersion);
        })
      )
      .subscribe();
  }

  fakeReloadMasterclasses(versionToSelect?: IMasterclassVersion): void {
    this.masterclassVersions.push(versionToSelect);
    this.masterclassVersions = this.masterclassVersions.filter((mv) => !mv.isTemporary);
    this.masterclassVersions[0].id === this.selectedELearningVersion.id ? this.masterclassVersions.splice(1, 1) : this.masterclassVersions.shift();
    this.selectVersion(versionToSelect);
  }

  selectVersion(version: IMasterclassVersion) {
    this.isMasterclassFormDirty = false;
    this.selectedMasterclassVersion = version;
    this.previousSelectedMasterclassVersion = version;
    this.selectedMasterclasses = version.masterclasses;
    this.selectedELearningElement = null;
  }

  async signOut(): Promise<void> {
    await this.firebaseService.signOut();
    this.router.navigate(['/admin']);
  }

  isUserTheBoss(): boolean {
    return this.currentUser.email === 'mickael@mymusicads.com' || this.currentUser.email === 'dlamarque.pro@gmail.com';
  }

  openPromoteUserDialog(): void {
    this.dialog
      .open(PromoteUserDialogComponent, {
        width: '400px',
        panelClass: 'promote-user-dialog',
        data: {
          currentUser: this.currentUser
        }
      })
      .afterClosed()
      .subscribe((promoteUserData: { email: string; selectedRole: UserRoleEnum }) => {
        if (promoteUserData) {
          const user = this.userDataSource.data.find((user) => user.email === promoteUserData.email);
          user.role = promoteUserData.selectedRole;
          this.updateUser(user);
        }
      });
  }

  getFacebookUrl(user: IUser) {
    if (!user.campaignInfo) return '';
    return user.campaignInfo.facebookUrl ? user.campaignInfo.facebookUrl : 'Non défini';
  }

  getInstagramUrl(user: IUser) {
    if (!user.campaignInfo) return '';
    return user.campaignInfo.instaUrl ? user.campaignInfo.instaUrl : 'Non défini';
  }

  getYouTubeEmail(user: IUser) {
    if (!user.campaignInfo) return '';
    return user.campaignInfo.youtubeEmail ? user.campaignInfo.youtubeEmail : 'Non défini';
  }

  getGoogleAdsId(user: IUser) {
    if (!user.campaignInfo) return '';
    return user.campaignInfo.googleAdsId ? user.campaignInfo.googleAdsId : 'Non défini';
  }

  getBusinessManagerId(user: IUser) {
    if (!user.campaignInfo) return '';
    return user.campaignInfo.businessManagerId ? user.campaignInfo.businessManagerId : 'Non défini';
  }

  private updateUser(user: IUser) {
    this.subs.sink = this.backOfficeService.updateUser(user).pipe(take(1)).subscribe();
  }

  determineUserRoleFr(role: UserRoleEnum) {
    if (role === UserRoleEnum.SUBSCRIBER) {
      return 'abonné';
    }
    if (role === UserRoleEnum.COACH) {
      return 'coach';
    }
    if (role === UserRoleEnum.TRAINER) {
      return 'formateur';
    }
    if (role === UserRoleEnum.ADMIN) {
      return 'admin';
    }
    return 'utilisateur';
  }

  resetUserPassword(user: IUser) {
    this.firebaseService.sendPasswordResetEmail(user.email).then(() => window.alert(`Relance envoyée par mail à l'utilisateur ${user.email}`));
  }

  deleteUser(user: IUser) {
    this.subs.sink = this.backOfficeService.deleteUser(user).subscribe(() => {
      this.userDataSource.data = this.userDataSource.data.filter((oldUser) => user.id !== oldUser.id);
      window.alert(`Utilisateur ${user.email} supprimé !`);
    });
  }

  notifyUserChange(user?: IUser) {
    this.userChanged.next(user);
  }

  updateTabIndex(tabIndex: number) {
    switch (tabIndex) {
      case 0:
        this.currentTab = 'campaigns';
        break;
      case 1:
        this.currentTab = 'users';
        break;
      case 2:
        this.currentTab = 'masterclasses';
        break;
      default:
        this.currentTab = 'campaigns';
        break;
    }
  }

  getTrainerNamesFromUsers(users: IUser[], trainerIds: string[]): string {
    return getTrainerNames(users, trainerIds ?? []);
  }

  get isCurrentTabCampaigns() {
    return this.currentTab === 'campaigns';
  }

  get isCurrentTabUsers() {
    return this.currentTab === 'users';
  }

  get isCurrentTabMasterclasses() {
    return this.currentTab === 'masterclasses';
  }

  public exportELearningDataForUser(user: IUser): void {
    console.log('=== DEBUT EXPORT ===');
    console.log('User:', user.email);
    console.log('isExportV2Enabled:', this.isExportV2Enabled);

    this.subs.sink = this.backOfficeService.getELearningDataForUser(user.id).subscribe((data) => {
      console.log('Data récupérée:', data);
      console.log('data.trainingType:', data?.trainingType);
      console.log('data.learningPeriod:', data?.learningPeriod);
      console.log('data.connectionsV2 length:', data?.connectionsV2?.length);

      if (data?.formations?.length) {
        console.log('Formations présentes, export par formation');
        const certificates = data.formations.map((formation) => this.buildCertificateForFormation(data, formation)).filter((certificate): certificate is string => !!certificate);
        if (!certificates.length) return;
        this.subs.sink = this.backOfficeService.updateELearningDataForUser(user.id, data).subscribe(() => {
          this.convertToPdf(`<body>${certificates.join('')}</body>`);
        });
        return;
      }

      const completedModules = this.getCompletedModules(data, data?.trainingType);
      console.log('Modules complétés:', completedModules);

      let conversionResult = '';
      if (this.isExportV2Enabled) {
        console.log('Mode V2 activé');

        if (!data?.learningPeriod) {
          console.log('Pas de learningPeriod');
          alert(`Veuillez rentrer une période d'apprentissage pour l'utilisateur ${user.email}.`);
          return;
        }

        if (!data?.trainingType) {
          console.log('Pas de trainingType');
          alert(`Veuillez rentrer un type de formation pour l'utilisateur ${user.email}.`);
          return;
        }

        console.log('Vérification connectionsV2:', !data.connectionsV2?.length);

        if (this.shouldRegenerateConnections(data.connectionsV2, data.learningPeriod)) {
          console.log('Génération de nouvelles connectionsV2');
          data.connectionsV2 = this.generateConnectionV2Data(data.learningPeriod, data.trainingType);
          console.log('Nouvelles connectionsV2 générées:', data.connectionsV2.length);

          if (!data.connectionsV2.length) {
            console.log('Aucune connexion générée, arrêt');
            return;
          }
        } else {
          console.log('ConnexionsV2 existantes trouvées, pas de régénération');
        }

        this.subs.sink = this.backOfficeService.updateELearningDataForUser(data.user.id, data).subscribe((eLearningData) => {
          console.log('Données mises à jour, génération du rapport');
          conversionResult = this.eLearningDataParsingService.exportforUser(eLearningData.connectionsV2, data.user);
          const fileContent = `<body>
          ${conversionResult}
          </body>`;
          this.convertToPdf(fileContent);
        });
      } else {
        console.log('Mode V1 (ancien)');
        const connections = this.buildCertifiedConnections(data.connections, completedModules, data.learningPeriod);
        conversionResult = this.eLearningDataParsingService.exportforUser(connections, data.user);
        if (!conversionResult) {
          window.alert(`L'utilisateur ${user.email} n'a pas de données de connexion : aucun module validé ni période d'apprentissage renseignée.`);
          return;
        }

        const fileContent = `<body>
        ${conversionResult}
        </body>`;
        this.convertToPdf(fileContent);
      }
    });
  }

  private buildCertificateForFormation(data: IELearningData, formation: IFormation): string | null {
    const learningPeriod = formation.learningPeriod;
    if (!learningPeriod?.start || !learningPeriod?.end) {
      alert(`Veuillez rentrer une période d'apprentissage pour chaque formation de l'utilisateur ${data.user?.email}.`);
      return null;
    }
    if (!formation.trainingType) {
      alert(`Veuillez rentrer un type pour chaque formation de l'utilisateur ${data.user?.email}.`);
      return null;
    }

    if (this.isExportV2Enabled) {
      if (this.shouldRegenerateConnections(formation.connectionsV2, learningPeriod)) {
        formation.connectionsV2 = this.generateConnectionV2Data(learningPeriod, formation.trainingType);
        if (!formation.connectionsV2.length) {
          return null;
        }
      }
      return this.eLearningDataParsingService.exportforUser(formation.connectionsV2, data.user);
    }

    const completedModules = this.getCompletedModules(data, formation.trainingType, formation.masterclassIds);
    const connections = this.buildCertifiedConnections(data.connections, completedModules, learningPeriod);
    const certificate = this.eLearningDataParsingService.exportforUser(connections, data.user);
    if (!certificate) {
      alert(`Aucun module validé ni connexion dans la période pour cette formation de l'utilisateur ${data.user?.email}.`);
    }
    return certificate;
  }

  exportFormationForUser(user: IUser, formationId: string): void {
    this.subs.sink = this.backOfficeService.getELearningDataForUser(user.id).subscribe((data) => {
      const formation = data?.formations?.find((f) => f.id === formationId) || this.getFormationsForUser(user).find((f) => f.id === formationId);
      if (!formation) return;
      const certificate = this.buildCertificateForFormation(data, formation);
      if (!certificate) return;
      this.subs.sink = this.backOfficeService.updateELearningDataForUser(user.id, data).subscribe(() => {
        this.convertToPdf(`<body>${certificate}</body>`);
      });
    });
  }

  // Completing a module is what credits e-learning hours, so the statement reports those hours on the
  // days the learner actually logged in — the tracked durations only cover time between two visibility
  // changes and would certify almost nothing.
  private buildCertifiedConnections(connections: ITimeInterval[] | undefined, completedModules: ICompletedModule[], learningPeriod?: ITimeInterval): ITimeInterval[] {
    const realConnections = (connections || []).filter(
      (connection) => !learningPeriod?.start || !learningPeriod?.end || !this.isConnectionOutsideLearningPeriod(connection, learningPeriod)
    );
    const creditedMinutes = getCreditedMinutes(completedModules);
    if (!creditedMinutes) {
      return realConnections;
    }
    return buildCreditedConnections({ creditedMinutes, realConnections, learningPeriod });
  }

  private isConnectionOutsideLearningPeriod(connection: ITimeInterval, learningPeriod: ITimeInterval): boolean {
    return moment(connection.start).isBefore(moment(learningPeriod.start).startOf('day')) || moment(connection.end).isAfter(moment(learningPeriod.end).endOf('day'));
  }

  // Stored generated logs are discarded when out of period or crossing midnight (bad data from earlier UTC-shifted generations)
  private shouldRegenerateConnections(connections: ITimeInterval[] | undefined, learningPeriod: ITimeInterval): boolean {
    if (!connections?.length) {
      return true;
    }
    return connections.some(
      (connection) => this.isConnectionOutsideLearningPeriod(connection, learningPeriod) || !moment(connection.start).isSame(moment(connection.end), 'day')
    );
  }

  private getCompletedModules(data: IELearningData | null | undefined, trainingType?: TrainingType, masterclassIds?: string[]): ICompletedModule[] {
    if (!this.selectedMasterclassVersion?.masterclasses) {
      return [];
    }

    const progress = {
      completedCourses: data?.completedCourses || [],
      deliverableAnswers: data?.deliverableAnswers || [],
      MCQAnswers: data?.MCQAnswers || []
    };
    const completedModules: ICompletedModule[] = [];

    for (const masterclass of this.selectedMasterclassVersion.masterclasses) {
      if (masterclassIds?.length && !masterclassIds.includes(masterclass.id)) continue;
      for (const module of masterclass.modules) {
        if (!module.chapters.some((chapter) => chapter.courses.length)) continue;

        if (isModuleCompleted(module, progress, trainingType)) {
          completedModules.push({
            name: module.name,
            duration: module.duration,
            masterclassName: masterclass.name
          });
        }
      }
    }

    return completedModules;
  }

  public exportMCQDataForUser(user: IUser): void {
    this.subs.sink = this.backOfficeService.getELearningDataForUser(user.id).subscribe((data) => {
      if (data) {
        const conversionResult = this.eLearningDataParsingService.exportMCQforUser(data.MCQAnswers, data.user);
        if (conversionResult) {
          const fileContent = `<body>
            ${conversionResult}
            </body>`;
          this.convertToPdf(fileContent);
        } else {
          console.warn(`Aucune donnée à exporter pour l'utilisateur ${user.email}.`);
        }
      } else {
        console.warn(`Aucune donnée trouvée pour l'utilisateur ${user.email}.`);
      }
    });
  }

  public exportELearningDataForAllUsers(): void {
    this.subs.sink = this.backOfficeService.getELearningDataForAll().subscribe((eLearningDatas) => {
      const conversionResults = eLearningDatas.map((data) => this.eLearningDataParsingService.exportforUser(data.connections, data.user)).filter((data) => !!data);
      const result = `
      <body>
      ${conversionResults.join('')}
      </body>
      `;
      this.convertToPdf(result);
    });
  }

  private getTrainingDurationHours(trainingType: TrainingType): number {
    console.log(`Analyse du type de formation: "${trainingType}"`);

    // Extraction du nombre d'heures depuis le nom du type de formation
    // Pattern pour capturer les nombres suivis de 'h' ou 'H'
    const match = trainingType.match(/(\d+)[hH]/);
    if (match) {
      const hours = parseInt(match[1]);
      console.log(`Heures extraites: ${hours}`);
      return hours;
    }

    // Cas particuliers
    switch (trainingType) {
      case 'Facture':
        console.log('Type Facture détecté, retour 18h');
        return 18;
      default:
        console.log(`Type non reconnu: "${trainingType}", retour 28h par défaut`);
        return 28;
    }
  }

  generateConnectionV2Data(learningPeriod: ITimeInterval | undefined, trainingType: TrainingType): ITimeInterval[] {
    if (!learningPeriod?.start || !learningPeriod?.end) {
      return [];
    }

    if (new Date(learningPeriod.end).getTime() > new Date().getTime()) {
      alert("La période d'apprentissage se termine après la date du jour. Veuillez la modifier pour utiliser l'export v2.");
      return [];
    }

    // Private lessons are not followed on the e-learning, so they are not part of the connection hours
    const deduction = trainingType === 'Facture' ? 0 : PRIVATE_LESSON_DEDUCTION_HOURS;
    const targetHours = this.getTrainingDurationHours(trainingType) - deduction;
    const sign = Math.random() > 0.5 ? 1 : -1;
    const randomizedHours = targetHours + sign * targetHours * TRAINING_DURATION_VARIATION * Math.random();

    return buildCreditedConnections({ creditedMinutes: Math.round(randomizedHours * 60), learningPeriod });
  }

  public convertToPdf(html: string): void {
    const iframe = document.getElementById('pdf-iframe') as HTMLIFrameElement;
    const doc = iframe.contentDocument || iframe.contentWindow?.document;

    doc.open();
    doc.write(html);
    doc.close();

    void this.printWhenImagesReady(iframe, doc);
  }

  private async printWhenImagesReady(iframe: HTMLIFrameElement, doc: Document): Promise<void> {
    await waitForImages(doc, PRINT_IMAGES_TIMEOUT_MS);
    iframe.contentWindow?.focus();
    iframe.contentWindow?.print();
  }

  createNewElement(elementInfo: ELearningElementInfo, elementType?: string) {
    if (!this.selectedELearningElement) {
      this.selectedMasterclasses.push(elementInfo as IMasterclass);
    } else {
      if (this.selectedELearningElement.type === 'course') {
        const element = this.backOfficeELearningService.determineElementChildren(this.selectedELearningElement, this.selectedMasterclasses)[0] as ICourse;
        if (elementType === 'deliverableQuestion') {
          if (!element.deliverableQuestions) element.deliverableQuestions = [];
          element.deliverableQuestions = [...element.deliverableQuestions, elementInfo as any];
        } else {
          let elementInfosMcq = elementInfo as any;
          if (!element.mcq) element.mcq = [];
          element.mcq = [...element.mcq, elementInfosMcq];
        }
      } else {
        this.backOfficeELearningService.determineElementChildren(this.selectedELearningElement, this.selectedMasterclasses).push(elementInfo);
      }
    }
    this.isMasterclassFormDirty = true;
  }

  openNewElement(element: IELearningElement) {
    const parentIds = [...(this.selectedELearningElement?.parentIds ?? [])];
    if (this.selectedELearningElement) {
      parentIds.push(this.selectedELearningElement.info.id);
    }
    const newSelectedElement = { ...element, parentIds };
    this.selectedELearningElement = newSelectedElement;
  }

  editElement(element: IELearningElement): void {
    const elementType = element.type;
    if (elementType === 'masterclass') {
      this.selectedMasterclasses = this.selectedMasterclasses.map((masterclass) => (masterclass.id === element.info.id ? (element.info as IMasterclass) : masterclass));
    } else if (elementType === 'module') {
      const targetMasterclass = this.selectedELearningElement.info as IMasterclass;
      targetMasterclass.modules = targetMasterclass.modules.map((module) => (module.id === element.info.id ? (element.info as IModule) : module));
    } else if (elementType === 'chapter') {
      const targetModule = this.selectedELearningElement.info as IModule;
      targetModule.chapters = targetModule.chapters.map((chapter) => (chapter.id === element.info.id ? (element.info as IChapter) : chapter));
    } else if (elementType === 'course') {
      const targetChapter = this.selectedELearningElement.info as IChapter;
      targetChapter.courses = targetChapter.courses.map((course) => (course.id === element.info.id ? (element.info as ICourse) : course));
    } else if (elementType === 'courseContent') {
      const targetCourse = this.selectedELearningElement.info as ICourse;
      targetCourse.mcq = targetCourse.mcq.map((mcq) => (mcq.id === element.info.id ? (element.info as any) : mcq));
    } else if (elementType === 'deliverableQuestion') {
      const targetCourse = this.selectedELearningElement.info as ICourse;
      if (targetCourse.deliverableQuestions) {
        targetCourse.deliverableQuestions = targetCourse.deliverableQuestions.map((q) => (q.id === element.info.id ? (element.info as any) : q));
      }
    }
    this.isMasterclassFormDirty = true;
  }

  changeElementIndex(initialPosition: number, newPosition: number) {
    this.isMasterclassFormDirty = true;
    const elements = this.backOfficeELearningService.determineElementChildren(this.selectedELearningElement, this.selectedMasterclasses);
    [elements[initialPosition], elements[newPosition]] = [elements[newPosition], elements[initialPosition]];
  }

  deleteElement(element: IELearningElement) {
    const elementType = element.type;
    if (elementType === 'masterclass') {
      this.selectedMasterclasses = this.selectedMasterclasses.filter((masterclass) => masterclass.id !== element.info.id);
    } else if (elementType === 'module') {
      const targetMasterclass = this.selectedELearningElement.info as IMasterclass;
      targetMasterclass.modules = targetMasterclass.modules.filter((module) => module.id !== element.info.id);
    } else if (elementType === 'chapter') {
      const targetModule = this.selectedELearningElement.info as IModule;
      targetModule.chapters = targetModule.chapters.filter((chapter) => chapter.id !== element.info.id);
    } else if (elementType === 'course') {
      const targetChapter = this.selectedELearningElement.info as IChapter;
      targetChapter.courses = targetChapter.courses.filter((course) => course.id !== element.info.id);
    } else if (elementType === 'deliverableQuestion') {
      const targetCourse = this.selectedELearningElement.info as ICourse;
      if (targetCourse.deliverableQuestions) {
        targetCourse.deliverableQuestions = targetCourse.deliverableQuestions.filter((q) => q.id !== (element.info as any).id);
      }
    }
    this.isMasterclassFormDirty = true;
  }

  openNewVersionDialog(): void {
    this.dialog
      .open(NewVersionFormDialogComponent, {
        width: '700px',
        panelClass: 'new-version-dialog',
        data: { masterclasses: this.selectedMasterclasses }
      })
      .afterClosed()
      .subscribe((version: IMasterclassVersion) => {
        if (version) {
          this.subs.sink = this.saveVersion$(version).subscribe();
        }
      });
  }

  saveVersion$(version: IMasterclassVersion): Observable<IMasterclassVersion> {
    if (!version.isTemporary && !confirmDuplicateCourseIds(version.masterclasses)) {
      return EMPTY;
    }
    this.masterclassLoading = true;
    return this.backOfficeService.createMasterclassVersion(version).pipe(
      tap(() => {
        if (!version.isTemporary) {
          this.fakeReloadMasterclasses(version);
          window.alert('Version sauvegardée avec succès!');
        }
        this.masterclassLoading = false;
      }),
      catchError((error) => {
        this.masterclassLoading = false;
        window.alert('Une erreur est survenue. Erreur : ' + error);
        return throwError(() => error);
      })
    );
  }

  onVisualizeClicked(): void {
    if (this.isMasterclassFormDirty) {
      const version: IMasterclassVersion = {
        id: uuid(),
        creationDate: new Date(),
        masterclasses: this.selectedMasterclasses,
        isTemporary: true
      };
      this.subs.sink = this.saveVersion$(version).subscribe(() => {
        this.redirectToVisualization(version.id);
      });
    } else {
      this.redirectToVisualization(this.selectedMasterclassVersion.id);
    }
  }

  redirectToVisualization(versionId: string): void {
    const url = this.router.serializeUrl(this.router.createUrlTree([`/e-learning/visualize/${versionId}/masterclasses`]));

    window.open(url, '_blank');
  }

  onVersionChangeClicked(version: IMasterclassVersion): void {
    setTimeout(() => {
      this.selectedMasterclassVersion = this.previousSelectedMasterclassVersion;
    }, 1);
    if (this.isMasterclassFormDirty) {
      this.openConfirmVersionChange(version);
    } else {
      this.selectVersion(version);
    }
  }

  openConfirmVersionChange(version: IMasterclassVersion): void {
    this.dialog
      .open(ConfirmVersionChangeDialogComponent, {
        width: '700px',
        panelClass: 'confirm-version-change-dialog',
        data: { version }
      })
      .afterClosed()
      .subscribe((save) => {
        if (save) {
          this.openNewVersionDialog();
        } else if (save === false) {
          this.selectVersion(version);
        } else {
          this.selectedMasterclassVersion = this.previousSelectedMasterclassVersion;
        }
      });
  }

  goToPreviousELearningElement(): void {
    let resultElement: ISelectedELearningElement | null = null;
    const targetElementId = this.selectedELearningElement.parentIds[this.selectedELearningElement.parentIds.length - 1];
    const parentIds = this.selectedELearningElement.parentIds?.slice(0, -1);
    if (this.selectedELearningElement.type === 'module') {
      resultElement = { info: this.selectedMasterclasses.find((mc) => mc.id === targetElementId), type: 'masterclass', parentIds };
    } else if (this.selectedELearningElement.type === 'chapter') {
      const masterclassId = this.selectedELearningElement.parentIds[0];
      const targetMasterclass = this.selectedMasterclasses.find((mc) => mc.id === masterclassId);
      const newElement = targetMasterclass?.modules?.find((module) => module.id === targetElementId);
      if (newElement) {
        resultElement = { info: newElement, type: 'module', parentIds };
      }
    } else if (this.selectedELearningElement.type === 'course') {
      const masterclassId = this.selectedELearningElement.parentIds[0];
      const moduleId = this.selectedELearningElement.parentIds[1];
      const targetMasterclass = this.selectedMasterclasses.find((mc) => mc.id === masterclassId);
      const targetModule = targetMasterclass?.modules?.find((module) => module.id === moduleId);
      const newElement = targetModule?.chapters.find((chapter) => chapter.id === targetElementId);
      if (newElement) {
        resultElement = { info: newElement, type: 'chapter', parentIds };
      }
    }
    this.selectedELearningElement = resultElement;
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }
}
